import type { AccessLevelProp, PaneFooterProps } from '@meterup/atto';
import type { RowSelectionState } from '@tanstack/react-table';
import {
  AlignStack,
  atContainers,
  Button,
  containers,
  ControlGroup,
  Divider,
  FeatureBadge,
  HStack,
  PaneFooter,
  Select,
  SelectItem,
  Small,
  styled,
  Text,
  TextInput,
} from '@meterup/atto';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SharedColumnDef } from '../createColumnBuilder';
import type { AutoCollectionStateInterface, Table } from '../utils';
import { getTablePaginationSelectionAlerts } from '../AutoCollectionSelectionAlert';

const TablePaginationDivider = styled(Divider, {
  padding: '$2 0',
});
TablePaginationDivider.displayName = 'TablePaginationDivider';

const TablePaginationControls = styled(AlignStack);
TablePaginationControls.displayName = 'TablePaginationControls';

const TablePaginationSummary = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$12',
  minWidth: 0,
});
TablePaginationSummary.displayName = 'TablePaginationSummary';

const TablePaginationLimit = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$12',
  minWidth: 0,

  ...atContainers([containers.pane.narrow, containers.drawer.all], {
    display: 'none',
  }),
});
TablePaginationLimit.displayName = 'TablePaginationLimit';

const TablePaginationContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$12',
  width: '100%',
});
TablePaginationContainer.displayName = 'TablePaginationContainer';

function PrevPageButton({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  return (
    <Button
      onClick={onClick}
      appearance="muted"
      arrangement="hidden-label"
      disabled={disabled}
      icon="arrow-left"
      variant="secondary"
    >
      Previous page
    </Button>
  );
}

function NextPageButton({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  return (
    <Button
      onClick={onClick}
      appearance="muted"
      arrangement="hidden-label"
      disabled={disabled}
      icon="arrow-right"
      variant="secondary"
      size="small"
    >
      Next page
    </Button>
  );
}

function nextTableOffset(offset: number, newLimit: number): number {
  return Math.floor(offset / newLimit) * newLimit;
}

const DEFAULT_LIMIT_OPTIONS = [10, 25, 50, 100];

export function TablePagination({
  offset,
  setOffset,
  limit,
  setLimit,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  total,
  numEntriesOnPage,
  objectLabel,
}: {
  offset: number;
  setOffset: (value: number) => void;
  limit: number;
  setLimit?: (value: number) => void;
  total: number;
  limitOptions?: number[];
  numEntriesOnPage: number;
  objectLabel?: React.ReactNode;
}) {
  const totalPages = total ? Math.ceil(total / limit) : 1;
  const page = Math.floor(offset / limit) + 1;

  const hasMore = useMemo(
    () => offset + numEntriesOnPage < total,
    [numEntriesOnPage, total, offset],
  );

  const handlePageChange = (newPage: string) => {
    const pageNum = Number(newPage);
    if (!Number.isNaN(pageNum)) {
      setOffset((pageNum - 1) * limit);
    }
  };

  const pageCounts = useMemo(() => {
    const PAGES_TO_SHOW = 40;
    const halfPages = Math.floor(PAGES_TO_SHOW / 2);
    let startPage = Math.max(1, page - halfPages);
    const endPage = Math.min(totalPages, startPage + PAGES_TO_SHOW - 1);

    if (endPage - startPage + 1 < PAGES_TO_SHOW) {
      startPage = Math.max(1, endPage - PAGES_TO_SHOW + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i += 1) {
      pages.push({
        key: String(i),
        value: String(i),
      });
    }

    if (startPage > 1) {
      pages.unshift({
        key: '1',
        value: '1',
      });
      if (startPage > 2) {
        pages.splice(1, 0, {
          key: '...',
          value: '...',
        });
      }
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push({
          key: '...',
          value: '...',
        });
      }
      pages.push({
        key: String(totalPages),
        value: String(totalPages),
      });
    }

    return pages;
  }, [page, totalPages]);

  const viewingStart = Math.min(offset + 1, total);
  const viewingEnd = Math.min(offset + numEntriesOnPage, total);

  return (
    <TablePaginationContainer>
      <TablePaginationControls
        align="center"
        direction="row"
        gap={4}
        preset="small"
        width="min-content"
        start={
          <ControlGroup relation="separate" size="small">
            <PrevPageButton onClick={() => setOffset(offset - limit)} disabled={page <= 1} />
            <Select
              value={String(page)}
              onValueChange={handlePageChange}
              items={pageCounts}
              aria-label="Select page"
              width="4.8rem"
            >
              {({ key, value }) => (
                <SelectItem key={key} textValue={value}>
                  {value}
                </SelectItem>
              )}
            </Select>
          </ControlGroup>
        }
        end={
          <NextPageButton
            onClick={() => setOffset(offset + limit)}
            disabled={!hasMore || page >= totalPages}
          />
        }
      >
        <Text whitespace="no-wrap">
          of <Text weight="bold">{totalPages}</Text>
        </Text>
      </TablePaginationControls>
      <TablePaginationSummary>
        <TablePaginationDivider appearance="thick" direction="column" />
        <Small whitespace="no-wrap">
          <Text weight="bold">
            {viewingStart} – {viewingEnd}
          </Text>{' '}
          {setLimit ? (
            <Select
              value={limit.toString()}
              onValueChange={(val) => {
                const newLimit = Number(val);
                setLimit(newLimit);
                setOffset(nextTableOffset(offset, newLimit));
              }}
              aria-label="Select page size"
              width="8rem"
              items={limitOptions.map((n) => ({
                key: String(n),
                value: String(n),
              }))}
            >
              {({ key, value }) => (
                <SelectItem key={key} textValue={key}>
                  <Small>
                    {value} ({key})
                  </Small>
                </SelectItem>
              )}
            </Select>
          ) : (
            <Small whitespace="no-wrap">
              <Text weight="bold">
                {viewingStart} – {viewingEnd}
              </Text>
            </Small>
          )}
          of <Text weight="bold">{total}</Text> {objectLabel ?? 'items'}
        </Small>
      </TablePaginationSummary>
    </TablePaginationContainer>
  );
}

const PAGE_THRESHOLD_FOR_TEXT_INPUT = 40;

interface LimitOption {
  value: number;
  label?: string;
  accessLevel?: AccessLevelProp | null;
}

interface TablePaginationEnhancedBaseProps {
  label?: React.ReactNode;
  limitOptions?: (number | LimitOption)[];
  showTotal?: boolean;
  showLimit?: boolean;
}

interface TablePaginationEnhancedClientProps<D, C extends SharedColumnDef<D, any>>
  extends TablePaginationEnhancedBaseProps {
  tableState: Pick<
    AutoCollectionStateInterface<D, C['id']>,
    'tableRef' | 'limit' | 'offset' | 'numRows' | 'setOffset' | 'setLimit'
  >;
}

interface TablePaginationEnhancedServerProps<D> extends TablePaginationEnhancedBaseProps {
  table: Table<D>;
}

export type TablePaginationEnhancedProps<D, C extends SharedColumnDef<D, any>> =
  | (TablePaginationEnhancedClientProps<D, C> & { serverSide?: false })
  | (TablePaginationEnhancedServerProps<D> & { serverSide: true });

export interface TablePaginationSelectionProps {
  multiSelectedRows?: RowSelectionState;
  onClearSelection?: () => void;
}

export type TablePaginationFooterProps<
  D,
  C extends SharedColumnDef<D, any>,
> = TablePaginationEnhancedProps<D, C> &
  TablePaginationSelectionProps &
  Omit<PaneFooterProps, 'alerts' | 'secondaryActions'>;

function getClientTableRowCount<D, C extends SharedColumnDef<D, any>>(
  tableState: Pick<AutoCollectionStateInterface<D, C['id']>, 'tableRef' | 'numRows'>,
) {
  return tableState.numRows || tableState.tableRef.current?.filteredRows.length || 0;
}

function normalizeLimitOptions(limitOptions: (number | LimitOption)[]): LimitOption[] {
  return limitOptions
    .map((o) =>
      typeof o === 'number'
        ? {
            value: o,
            label: String(o),
          }
        : o,
    )
    .filter((o) => o.accessLevel !== null);
}

function renderLimitOption(option: LimitOption) {
  return (
    <SelectItem key={option.value} textValue={option.label ?? String(option.value)}>
      <AlignStack
        end={
          option.accessLevel ? <FeatureBadge type={option.accessLevel} size="small" /> : undefined
        }
        gap={4}
      >
        {option.label ?? option.value}
      </AlignStack>
    </SelectItem>
  );
}

function ClientTablePaginationEnhanced<
  D extends any = any,
  C extends SharedColumnDef<D, any> = SharedColumnDef<D, any>,
>({
  tableState,
  label,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  showTotal = true,
  showLimit = true,
}: TablePaginationEnhancedClientProps<D, C>) {
  const total = getClientTableRowCount(tableState);
  const totalPages = total ? Math.ceil(total / tableState.limit) : 1;
  const page = Math.floor(tableState.offset / tableState.limit) + 1;
  const numEntriesOnPage = Math.min(Math.max(0, total - tableState.offset), tableState.limit);

  const [pageInputValue, setPageInputValue] = useState(String(page));

  useEffect(() => {
    setPageInputValue(String(page));
  }, [page]);

  const hasMore = useMemo(
    () => tableState.offset + numEntriesOnPage < total,
    [numEntriesOnPage, total, tableState.offset],
  );

  const handlePageChange = (newPage: string) => {
    const pageNum = Number(newPage);
    if (!Number.isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      tableState.setOffset((pageNum - 1) * tableState.limit);
    }
  };

  const handlePageInputSubmit = () => {
    const pageNum = Number(pageInputValue);
    if (Number.isNaN(pageNum) || pageInputValue === '') {
      setPageInputValue(String(page));
      return;
    }
    const clamped = Math.min(Math.max(pageNum, 1), totalPages);
    setPageInputValue(String(clamped));
    tableState.setOffset((clamped - 1) * tableState.limit);
  };

  const usesTextInput = totalPages >= PAGE_THRESHOLD_FOR_TEXT_INPUT;

  const pageCounts = useMemo(() => {
    if (usesTextInput) return [];
    const pages = [];
    for (let i = 1; i <= totalPages; i += 1) {
      pages.push({
        key: String(i),
        value: String(i),
      });
    }
    return pages;
  }, [totalPages, usesTextInput]);

  const viewingStart = Math.min(tableState.offset + 1, total);
  const viewingEnd = Math.min(tableState.offset + numEntriesOnPage, total);

  return (
    <TablePaginationContainer>
      {totalPages > 1 && (
        <TablePaginationControls
          align="center"
          direction="row"
          gap={4}
          preset="small"
          width="min-content"
          start={
            <ControlGroup relation="separate" size="small">
              <PrevPageButton
                onClick={() => tableState.setOffset(tableState.offset - tableState.limit)}
                disabled={page <= 1}
              />
              {usesTextInput ? (
                <TextInput
                  type="number"
                  inputProps={{ min: 1, max: totalPages }}
                  value={pageInputValue}
                  onChange={(val) => setPageInputValue(val ?? '')}
                  onBlur={handlePageInputSubmit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handlePageInputSubmit();
                    }
                  }}
                  aria-label="Go to page"
                  width="4.8rem"
                />
              ) : (
                <Select
                  value={String(page)}
                  onValueChange={handlePageChange}
                  items={pageCounts}
                  aria-label="Select page"
                  width="4.8rem"
                >
                  {({ key, value }) => <SelectItem key={key}>{value}</SelectItem>}
                </Select>
              )}
            </ControlGroup>
          }
          end={
            <NextPageButton
              onClick={() => tableState.setOffset(tableState.offset + tableState.limit)}
              disabled={!hasMore || page >= totalPages}
            />
          }
        >
          <Text whitespace="no-wrap">
            of <Text weight="bold">{totalPages}</Text>
          </Text>
        </TablePaginationControls>
      )}
      <TablePaginationSummary>
        {totalPages > 1 && <TablePaginationDivider appearance="thick" direction="column" />}
        <Small whitespace="no-wrap">
          <HStack align="center" spacing={4}>
            <Text weight="bold">{viewingStart}</Text> &ndash;{' '}
            <Text weight="bold">{viewingEnd}</Text>
            {showTotal && (
              <>
                {' '}
                of <Text weight="bold">{total}</Text>
              </>
            )}{' '}
            {label ?? 'items'}
          </HStack>
        </Small>
      </TablePaginationSummary>
      <TablePaginationLimit>
        <TablePaginationDivider appearance="thick" direction="column" />
        <Small whitespace="no-wrap">
          <HStack align="center" spacing={4}>
            {showLimit && (
              <AlignStack
                align="center"
                direction="row"
                gap={4}
                preset="small"
                width="min-content"
                start={
                  <Select
                    value={tableState.limit.toString()}
                    onValueChange={(val) => {
                      const newLimit = Number(val);
                      tableState.setLimit(newLimit);
                      tableState.setOffset(nextTableOffset(tableState.offset, newLimit));
                    }}
                    size="small"
                    width="3.8rem"
                    aria-label="Select page size"
                    items={normalizeLimitOptions(limitOptions)}
                  >
                    {renderLimitOption}
                  </Select>
                }
              >
                <Text whitespace="no-wrap">per page</Text>
              </AlignStack>
            )}
          </HStack>
        </Small>
      </TablePaginationLimit>
    </TablePaginationContainer>
  );
}

function ServerSideTablePagination<D extends any = any>({
  label,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  showTotal = true,
  showLimit = true,
  table,
}: TablePaginationEnhancedServerProps<D>) {
  const { page, goTo, setLimit } = table;
  const [pageNumber, setPageNumber] = useState<string>('1');
  const usesTextInput = (page?.totalPages ?? 1) >= 20;
  const viewingStart = Math.min(((page?.page ?? 1) - 1) * table.limit + 1, page?.totalCount ?? 0);
  const viewingEnd = Math.min(
    ((page?.page ?? 1) - 1) * table.limit + (page?.items.length ?? 0),
    page?.totalCount ?? 0,
  );
  const pageCounts = useMemo(() => {
    if (!page) {
      return [{ key: '1', value: '1' }];
    }
    if (usesTextInput) return [];
    const pages = [];
    for (let i = 1; i <= page.totalPages; i += 1) {
      pages.push({
        key: String(i),
        value: String(i),
      });
    }
    return pages;
  }, [page, usesTextInput]);
  useEffect(() => setPageNumber(String(page?.page ?? 1)), [page?.page]);

  const handlePageInputSubmit = useCallback(
    (inputPage: string) => {
      const n = parseInt(inputPage, 10);
      if (Number.isNaN(n)) {
        return;
      }
      goTo(n);
    },
    [goTo],
  );

  const handlePageChange = useCallback(
    (inputPage: number) => {
      goTo(inputPage);
    },
    [goTo],
  );

  return (
    <TablePaginationContainer>
      {page && page.totalPages > 1 && (
        <TablePaginationControls
          align="center"
          direction="row"
          gap={4}
          preset="small"
          width="min-content"
          start={
            <ControlGroup relation="separate" size="small">
              <PrevPageButton
                onClick={() => handlePageChange(page.page - 1)}
                disabled={page.page <= 1}
              />
              {usesTextInput ? (
                <TextInput
                  type="number"
                  inputProps={{ min: 1, max: page.totalPages }}
                  value={pageNumber}
                  onChange={setPageNumber}
                  onBlur={() => handlePageInputSubmit(pageNumber)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handlePageInputSubmit(pageNumber);
                    }
                  }}
                  aria-label="Go to page"
                  width="4.8rem"
                />
              ) : (
                <Select
                  value={page.page.toString(10)}
                  onValueChange={handlePageInputSubmit}
                  items={pageCounts}
                  aria-label="Select page"
                  width="4.8rem"
                >
                  {({ key, value }) => <SelectItem key={key}>{value}</SelectItem>}
                </Select>
              )}
            </ControlGroup>
          }
          end={
            <NextPageButton
              onClick={() => handlePageChange(page.page + 1)}
              disabled={page.page >= page.totalPages}
            />
          }
        >
          <Text whitespace="no-wrap">
            of <Text weight="bold">{page.totalPages}</Text>
          </Text>
        </TablePaginationControls>
      )}
      {page && (
        <TablePaginationSummary>
          {page.totalPages > 1 && <TablePaginationDivider appearance="thick" direction="column" />}
          <Small whitespace="no-wrap">
            <HStack align="center" spacing={4}>
              <Text weight="bold">{viewingStart}</Text> &ndash;{' '}
              <Text weight="bold">{viewingEnd}</Text>
              {showTotal && (
                <>
                  {' '}
                  of <Text weight="bold">{page.totalCount}</Text>
                </>
              )}{' '}
              {label ?? 'items'}
            </HStack>
          </Small>
        </TablePaginationSummary>
      )}
      <TablePaginationLimit>
        <TablePaginationDivider appearance="thick" direction="column" />
        <Small whitespace="no-wrap">
          <HStack align="center" spacing={4}>
            {showLimit && (
              <AlignStack
                align="center"
                direction="row"
                gap={4}
                preset="small"
                width="min-content"
                start={
                  <Select
                    value={table.limit.toString()}
                    onValueChange={(val) => {
                      const limit = parseInt(val, 10);
                      if (Number.isNaN(limit)) {
                        return;
                      }
                      setLimit(limit);
                    }}
                    size="small"
                    width="3.8rem"
                    aria-label="Select page size"
                    items={normalizeLimitOptions(limitOptions)}
                  >
                    {renderLimitOption}
                  </Select>
                }
              >
                <Text whitespace="no-wrap">per page</Text>
              </AlignStack>
            )}
          </HStack>
        </Small>
      </TablePaginationLimit>
    </TablePaginationContainer>
  );
}

// Enhanced component that works with TableState
export function TablePaginationEnhanced<
  D extends any = any,
  C extends SharedColumnDef<D, any> = SharedColumnDef<D, any>,
>(props: TablePaginationEnhancedProps<D, C>) {
  if (props.serverSide) {
    return (
      <ServerSideTablePagination
        table={props.table}
        label={props.label}
        limitOptions={props.limitOptions}
        showTotal={props.showTotal}
        showLimit={props.showLimit}
      />
    );
  }
  return (
    <ClientTablePaginationEnhanced
      tableState={props.tableState}
      label={props.label}
      limitOptions={props.limitOptions}
      showTotal={props.showTotal}
      showLimit={props.showLimit}
    />
  );
}

export function TablePaginationFooter<
  D extends any = any,
  C extends SharedColumnDef<D, any> = SharedColumnDef<D, any>,
>({
  multiSelectedRows,
  onClearSelection,
  actions,
  children,
  css,
  ...paginationProps
}: TablePaginationFooterProps<D, C>) {
  const totalRowCount = paginationProps.serverSide
    ? paginationProps.table.page?.totalCount ?? 0
    : getClientTableRowCount(paginationProps.tableState);

  const alerts = getTablePaginationSelectionAlerts({
    multiSelectedRows,
    onClearSelection,
    totalRowCount,
  });

  return (
    <PaneFooter
      alerts={alerts}
      secondaryActions={<TablePaginationEnhanced {...paginationProps} />}
      actions={actions}
      css={css}
    >
      {children}
    </PaneFooter>
  );
}
